import { type Projection, createProjection } from '@portfolio-pm/projection';
import { nextOccurrence } from '@portfolio-pm/recurrence';
import type {
  CheckIn,
  ExportData,
  KeyResult,
  Objective,
  Project,
  ReminderPreferences,
  SelectedTaskArrangement,
  SmartList,
  SubtaskDisplayT,
  Task,
  TaskActivity,
  TaskArrangement,
  UiPreferences,
  WorkflowColumn,
} from '@portfolio-pm/shared';
import { parseSmartAdd } from '@portfolio-pm/smart-add';
import { compileQuery } from '@portfolio-pm/smart-list';
import {
  type CreateCheckInInput,
  type CreateKeyResultInput,
  type CreateObjectiveInput,
  type CreateSmartListInput,
  type CreateTaskActivityInput,
  type CreateTaskArrangementInput,
  type CreateTaskInput,
  type CreateWorkflowColumnInput,
  INBOX_PROJECT_ID,
  type UpdateSmartListInput,
  type UpdateTaskArrangementInput,
  type UpdateTaskInput,
  archiveProject as archiveProjectInDoc,
  clearContent,
  completeTask,
  createCheckIn as createCheckInInDoc,
  createDoc,
  createKeyResult as createKeyResultInDoc,
  createObjective as createObjectiveInDoc,
  createProject as createProjectInDoc,
  createSmartList as createSmartListInDoc,
  createTask,
  createTaskActivity as createTaskActivityInDoc,
  createTaskArrangement as createTaskArrangementInDoc,
  createWorkflowColumn as createWorkflowColumnInDoc,
  deleteSmartList as deleteSmartListInDoc,
  deleteTaskActivity as deleteTaskActivityInDoc,
  deleteTaskArrangement as deleteTaskArrangementInDoc,
  deleteWorkflowColumn as deleteWorkflowColumnInDoc,
  ensureDefaultWorkflowColumns,
  exportDoc,
  getProjectsMap,
  getReminderPreferences as getReminderPreferencesFromDoc,
  getUiPreferences as getUiPreferencesFromDoc,
  importData as importDataIntoDoc,
  linkTaskToKeyResult as linkTaskToKeyResultInDoc,
  listProjects,
  renameProject as renameProjectInDoc,
  renameWorkflowColumn as renameWorkflowColumnInDoc,
  reopenTask,
  reorderProjects as reorderProjectsInDoc,
  reorderTasks as reorderTasksInDoc,
  reorderWorkflowColumns as reorderWorkflowColumnsInDoc,
  restoreTask as restoreTaskInDoc,
  runDefaultMigrations,
  seedInbox,
  selectTaskArrangement as selectTaskArrangementInDoc,
  setSelectedTaskSubtaskDisplay as setSelectedTaskSubtaskDisplayInDoc,
  trashTask,
  unarchiveProject as unarchiveProjectInDoc,
  unlinkTaskFromKeyResult as unlinkTaskFromKeyResultInDoc,
  updateKeyResult as updateKeyResultInDoc,
  updateObjective as updateObjectiveInDoc,
  updateReminderPreferences as updateReminderPreferencesInDoc,
  updateSmartList as updateSmartListInDoc,
  updateTask,
  updateTaskActivity as updateTaskActivityInDoc,
  updateTaskArrangement as updateTaskArrangementInDoc,
  updateUiPreferences as updateUiPreferencesInDoc,
} from '@portfolio-pm/yjs-doc';
import { useSyncExternalStore } from 'react';
import { IndexeddbPersistence } from 'y-indexeddb';
import * as Y from 'yjs';
import { smartAddToCreateInput } from './smart-add-bridge.ts';
import { SyncProvider, type SyncStatus } from './sync-provider.ts';

/**
 * Document store: a singleton Y.Doc + live projection, plus a tiny
 * useSyncExternalStore adapter so components re-render when the
 * projection fires its subscribe callback.
 *
 * Local persistence is wired through `y-indexeddb`: a single IDB
 * database (name: `DEFAULT_DB_NAME`) holds the Yjs update log, which
 * is replayed into the in-memory doc on store construction.
 * `whenSynced` resolves once that replay is complete so the app can
 * block first render and avoid the initial empty flash.
 *
 * In jsdom without a polyfilled `indexedDB`, persistence is a
 * no-op: `whenSynced` resolves immediately and `resetLocalData()`
 * does nothing. That keeps existing RTL tests green without forcing
 * a fake-indexeddb dependency on every test file.
 */
export interface DocStore {
  readonly doc: Y.Doc;
  readonly projection: Projection;
  /** Resolves once local persistence has replayed into the doc. */
  readonly whenSynced: Promise<void>;
  /** Live websocket sync status (`'disabled'` when no sync server). */
  useSyncStatus(): SyncStatus;
  /** All non-archived projects, sorted for default pickers. */
  useProjects(): Project[];
  /** All projects, including archived ones, sorted by persisted order. */
  useAllProjects(): Project[];
  /** The currently selected project (Inbox by default). */
  useActiveProject(): Project;
  /** Tasks belonging to the active project. */
  useTasks(): Task[];
  /** Full OKR snapshot used by the OKR workspace route. */
  useOkrSnapshot(): {
    objectives: Objective[];
    keyResults: KeyResult[];
    checkIns: CheckIn[];
    tasks: Task[];
  };
  /** Portfolio-wide snapshot used by the Focus workspace route. */
  useFocusSnapshot(): {
    projects: Project[];
    objectives: Objective[];
    keyResults: KeyResult[];
    tasks: Task[];
    openTasks: Task[];
    workflowColumns: WorkflowColumn[];
    smartLists: SmartList[];
    taskArrangements: TaskArrangement[];
    selectedTaskArrangements: SelectedTaskArrangement[];
    taskActivities: TaskActivity[];
  };
  /** Saved task views and workflow columns, sorted by their synced order. */
  useWorkflowColumns(): WorkflowColumn[];
  useSmartLists(): SmartList[];
  useTaskArrangements(): TaskArrangement[];
  useSelectedTaskArrangements(): SelectedTaskArrangement[];
  useTaskActivities(taskId: string | undefined): TaskActivity[];
  useTags(): Array<{ tag: string; count: number }>;
  /** Read-only progress helpers derived from projection indexes. */
  keyResultProgress(id: string): number;
  expectedKeyResultProgress(id: string, now: number): number;
  keyResultLag(id: string, now: number): number;
  objectiveProgress(id: string): number;
  expectedObjectiveProgress(id: string, now: number): number;
  objectiveLag(id: string, now: number): number;
  /** Returns true when any dependency task is still incomplete. */
  isTaskBlocked(id: string): boolean;
  /** Returns true when a task owns one or more child tasks. */
  hasChildTasks(id: string): boolean;
  /** Child tasks for a parent task. */
  childTasks(parentTaskId: string): Task[];
  /** Filter tasks through a saved-view query. */
  tasksMatchingQuery(query: string): Task[];
  /** Switch the active project. */
  setActiveProject(id: string): void;
  /** Create a new project and select it. Returns the created project. */
  createProject(name: string): Project | undefined;
  /** Rename a project (no-op on empty input). */
  renameProject(id: string, name: string): void;
  /** Archive a project. If it was active, fall back to the Inbox. */
  archiveProject(id: string): void;
  /** Unarchive a project. */
  unarchiveProject(id: string): void;
  /** Persist the manual sidebar order for projects. */
  reorderProjects(orderedVisibleIds: string[]): void;
  /**
   * Parse user-typed Smart Add text and write the resulting task into the
   * active project. Title-only input works too; unknown/empty input is a
   * no-op. Returns `true` when a task was actually created.
   */
  createTaskFromSmartAdd(text: string): boolean;
  /** Toggle a task between completed and open. */
  toggleTaskComplete(id: string): void;
  /** Rename a task (no-op on empty title). */
  renameTask(id: string, title: string): void;
  /** Generic task patch path used by the detail inspector. */
  updateTask(id: string, patch: UpdateTaskInput): void;
  /** Create a one-level child task under an existing task. */
  createChildTask(parentTaskId: string, title: string): Task | undefined;
  /** Duplicate a task as a fresh open task, preserving user-editable fields. */
  duplicateTask(id: string): Task | undefined;
  /** Persist the visible manual task sequence while retaining hidden task positions. */
  reorderTasks(orderedVisibleIds: string[]): void;
  /** Complete a task; optionally complete its direct children too. */
  completeTaskWithChildren(id: string, completeChildren: boolean): void;
  /** Set or clear an explicit task reminder. */
  setTaskReminder(id: string, reminderAt: number | undefined): void;
  /** Move a task to Trash. */
  removeTask(id: string, options?: { deleteSeries?: boolean; generateNext?: boolean }): void;
  restoreTask(id: string): void;
  undoLastChange(): void;
  /** Shared reminder preferences synced with the document. */
  useReminderPreferences(): ReminderPreferences;
  /** Update shared reminder preferences. */
  updateReminderPreferences(
    patch: Partial<
      Pick<
        ReminderPreferences,
        'dueNudgesEnabled' | 'dueLeadMinutes' | 'scheduledNudgesEnabled' | 'scheduledLeadMinutes'
      >
    >,
  ): void;
  /** Shared UI preferences synced with the document. */
  useUiPreferences(): UiPreferences;
  /** Update shared UI preferences. */
  updateUiPreferences(
    patch: Partial<
      Pick<
        UiPreferences,
        | 'priorityColors'
        | 'tagColors'
        | 'taskSwipeLeftToRight'
        | 'taskSwipeRightToLeft'
        | 'favoriteShortcuts'
        | 'projectSidebarSort'
        | 'projectSidebarCount'
        | 'projectSidebarShowArchived'
        | 'appBadgeCountMode'
      >
    >,
  ): void;
  /** Create a top-level or child objective. */
  createObjective(input: CreateObjectiveInput): Objective | undefined;
  /** Update an existing objective. */
  updateObjective(id: string, patch: Partial<CreateObjectiveInput>): void;
  /** Create a key result. */
  createKeyResult(input: CreateKeyResultInput): KeyResult | undefined;
  /** Update an existing key result. */
  updateKeyResult(id: string, patch: Partial<CreateKeyResultInput>): void;
  /** Link a task to a key result (same-project only). */
  linkTaskToKeyResult(taskId: string, keyResultId: string): boolean;
  /** Remove a task-to-KR link. */
  unlinkTaskFromKeyResult(taskId: string): void;
  /** Persist a daily/weekly check-in. */
  createCheckIn(input: CreateCheckInInput): CheckIn | undefined;
  /** Saved view CRUD. */
  createSmartList(input: CreateSmartListInput): SmartList | undefined;
  updateSmartList(id: string, patch: UpdateSmartListInput): void;
  removeSmartList(id: string): void;
  /** Common task arrangement views overlaid on project, system, smart, and tag lists. */
  createTaskArrangement(input: CreateTaskArrangementInput): TaskArrangement | undefined;
  updateTaskArrangement(id: string, patch: UpdateTaskArrangementInput): void;
  removeTaskArrangement(id: string): void;
  selectTaskArrangement(
    listKey: string,
    arrangementId: string,
  ): SelectedTaskArrangement | undefined;
  setTaskSubtaskDisplay(
    listKey: string,
    subtaskDisplay: SubtaskDisplayT,
  ): SelectedTaskArrangement | undefined;
  /** Global workflow column CRUD. */
  createWorkflowColumn(input: CreateWorkflowColumnInput): WorkflowColumn | undefined;
  renameWorkflowColumn(id: string, name: string): void;
  reorderWorkflowColumns(orderedIds: string[]): void;
  removeWorkflowColumn(id: string, replacementId?: string): void;
  /** Add an authored activity comment. */
  createTaskActivity(input: CreateTaskActivityInput): TaskActivity | undefined;
  /** Delete an activity comment by id. */
  deleteTaskActivity(id: string): void;
  /** Edit the message of an existing activity comment. */
  updateTaskActivity(id: string, message: string): void;
  /** Reset to a fresh workspace: wipe all content then re-seed inbox + default columns. Device preferences are kept. */
  clearUserData(): void;
  /** Wipe local persistence + in-memory state. Returns once done. */
  resetLocalData(): Promise<void>;
  /** Serialize the current Y.Doc to a plain ExportData envelope. */
  exportData(): ExportData;
  /**
   * Merge an ExportData envelope into the Y.Doc (last-write-wins per id).
   * Tombstoned items and records with an older updatedAt are skipped.
   */
  importData(data: ExportData): void;
}

/** IndexedDB database name used for the local Yjs store. */
export const DEFAULT_DB_NAME = 'portfolio-pm';

const EMPTY_TASK_ACTIVITIES: TaskActivity[] = [];

export interface DocStoreOptions {
  /**
   * Override the IndexedDB database name. Pass `null` to disable
   * persistence entirely (useful in tests).
   */
  dbName?: string | null;
  /**
   * Sync server URL (e.g. `wss://sync.example.com`). When omitted or
   * null the store stays in local-only mode (`SyncStatus = 'disabled'`).
   * Tests almost always leave this unset.
   */
  syncUrl?: string | null;
  /** Logical room name for the sync provider. Defaults to `'default'`. */
  syncRoom?: string;
  /** Dev/test-only remote room reset secret. */
  resetSecret?: string | null;
}

let singleton: DocStore | undefined;
let defaultOptions: DocStoreOptions = {};

/**
 * Configure the store that will be built on the next `getDocStore()` call.
 * Has no effect if a singleton already exists — call `resetDocStoreForTests()`
 * first to force a rebuild.
 */
export function configureDocStore(options: DocStoreOptions): void {
  defaultOptions = options;
}

export function getDocStore(): DocStore {
  if (!singleton) singleton = buildDocStore(defaultOptions);
  return singleton;
}

/** Test-only: tear down and replace the module-level singleton. */
export function resetDocStoreForTests(): void {
  if (singleton) {
    singleton.projection.destroy();
    singleton.doc.destroy();
    singleton = undefined;
  }
  defaultOptions = {};
}

function buildDocStore(options: DocStoreOptions): DocStore {
  const doc = createDoc();
  runDefaultMigrations(doc);
  // seedInbox is idempotent and uses a fixed id ('p_inbox'), so it is safe
  // to run it before persistence replays — the replay will either agree
  // (same id) or be a no-op.
  seedInbox(doc);

  const persistence = createPersistence(options, doc);
  const whenSynced: Promise<void> = persistence
    ? persistence.whenSynced.then(() => undefined)
    : Promise.resolve();

  // Optional websocket sync. Tests leave `syncUrl` unset, so the
  // provider stays in `disabled` mode and never opens a socket.
  const sync = new SyncProvider({
    doc,
    serverUrl: options.syncUrl ?? null,
    room: options.syncRoom ?? 'default',
  });
  const undoManager = new Y.UndoManager(ROOT_UNDO_TYPES.map((key) => doc.getMap(key)));
  const syncListeners = new Set<() => void>();
  sync.subscribe(() => {
    for (const listener of syncListeners) listener();
  });
  const subscribeSync = (listener: () => void): (() => void) => {
    syncListeners.add(listener);
    return () => {
      syncListeners.delete(listener);
    };
  };

  const projection = createProjection(doc);

  // Active project lives outside the CRDT — it's UI session state, not
  // shared between devices. Default to the Inbox.
  let activeProjectId: string = INBOX_PROJECT_ID;
  const activeListeners = new Set<() => void>();
  const notifyActive = (): void => {
    for (const listener of activeListeners) listener();
  };
  const subscribeActive = (listener: () => void): (() => void) => {
    activeListeners.add(listener);
    return () => {
      activeListeners.delete(listener);
    };
  };

  // Observe the projects Y.Map so the sidebar re-renders when projects
  // are added/renamed/archived. We rebuild a sorted projects snapshot on
  // each notify so React's useSyncExternalStore can do referential
  // equality short-circuiting between unrelated renders.
  let cachedAllProjects: Project[] = listProjects(doc);
  let cachedProjects: Project[] = filterAndSortProjects(cachedAllProjects);
  const projectListeners = new Set<() => void>();
  const projectsObserver = (): void => {
    cachedAllProjects = listProjects(doc);
    cachedProjects = filterAndSortProjects(cachedAllProjects);
    refreshFocusSnapshot();
    for (const listener of projectListeners) listener();
  };
  getProjectsMap(doc).observeDeep(projectsObserver);
  const subscribeProjects = (listener: () => void): (() => void) => {
    projectListeners.add(listener);
    return () => {
      projectListeners.delete(listener);
    };
  };

  // Tasks for the active project, sorted by creation time so renames
  // (which re-insert into the projection's Set indexes) don't shuffle
  // the visible order.
  const sortByCreated = (tasks: Task[]): Task[] =>
    [...tasks]
      .filter((task) => task.trashedAt === undefined)
      .sort((a, b) => a.createdAt - b.createdAt);
  let cachedActiveTasks: Task[] = sortByCreated(projection.byProject(activeProjectId));
  const refreshActiveTasks = (): void => {
    cachedActiveTasks = sortByCreated(projection.byProject(activeProjectId));
  };
  projection.subscribe(refreshActiveTasks);
  const taskListeners = new Set<() => void>();
  const subscribeTasks = (listener: () => void): (() => void) => {
    const off1 = projection.subscribe(listener);
    taskListeners.add(listener);
    return () => {
      off1();
      taskListeners.delete(listener);
    };
  };
  const notifyTasks = (): void => {
    for (const listener of taskListeners) listener();
  };

  const setActiveProject = (id: string): void => {
    if (activeProjectId === id) return;
    activeProjectId = id;
    refreshActiveTasks();
    notifyActive();
    notifyTasks();
  };

  const findProject = (id: string): Project | undefined =>
    cachedAllProjects.find((p) => p.id === id);
  const inboxOrFirst = (): Project => {
    const inbox = cachedProjects.find((p) => p.id === INBOX_PROJECT_ID);
    if (inbox) return inbox;
    const first = cachedProjects[0];
    if (first) return first;
    // Should not happen because seedInbox runs above, but be defensive.
    return {
      id: INBOX_PROJECT_ID,
      name: 'Inbox',
      inbox: true,
      archived: false,
      order: 0,
      createdAt: 0,
      updatedAt: 0,
    };
  };

  let cachedOkrSnapshot: {
    objectives: Objective[];
    keyResults: KeyResult[];
    checkIns: CheckIn[];
    tasks: Task[];
  } = {
    objectives: projection.getObjectives(),
    keyResults: projection.getKeyResults(),
    checkIns: projection.getCheckIns(),
    tasks: projection.getTasks(),
  };
  const refreshOkrSnapshot = (): void => {
    cachedOkrSnapshot = {
      objectives: projection.getObjectives(),
      keyResults: projection.getKeyResults(),
      checkIns: projection.getCheckIns(),
      tasks: projection.getTasks(),
    };
  };
  projection.subscribe(refreshOkrSnapshot);

  let cachedReminderPreferences = getReminderPreferencesFromDoc(doc);
  const reminderPreferencesListeners = new Set<() => void>();
  const reminderPreferencesObserver = (): void => {
    cachedReminderPreferences = getReminderPreferencesFromDoc(doc);
    for (const listener of reminderPreferencesListeners) listener();
  };
  doc.getMap('reminderPreferences').observeDeep(reminderPreferencesObserver);
  const subscribeReminderPreferences = (listener: () => void): (() => void) => {
    reminderPreferencesListeners.add(listener);
    return () => {
      reminderPreferencesListeners.delete(listener);
    };
  };

  let cachedUiPreferences = getUiPreferencesFromDoc(doc);
  const uiPreferencesListeners = new Set<() => void>();
  const uiPreferencesObserver = (): void => {
    cachedUiPreferences = getUiPreferencesFromDoc(doc);
    for (const listener of uiPreferencesListeners) listener();
  };
  doc.getMap('uiPreferences').observeDeep(uiPreferencesObserver);
  const subscribeUiPreferences = (listener: () => void): (() => void) => {
    uiPreferencesListeners.add(listener);
    return () => {
      uiPreferencesListeners.delete(listener);
    };
  };

  let cachedFocusSnapshot: {
    projects: Project[];
    objectives: Objective[];
    keyResults: KeyResult[];
    tasks: Task[];
    openTasks: Task[];
    workflowColumns: WorkflowColumn[];
    smartLists: SmartList[];
    taskArrangements: TaskArrangement[];
    selectedTaskArrangements: SelectedTaskArrangement[];
    taskActivities: TaskActivity[];
  } = {
    projects: cachedProjects,
    objectives: projection.getObjectives(),
    keyResults: projection.getKeyResults(),
    tasks: projection.getTasks(),
    openTasks: projection.getOpenTasks(),
    workflowColumns: projection.getWorkflowColumns(),
    smartLists: projection.getSmartLists(),
    taskArrangements: projection.getTaskArrangements(),
    selectedTaskArrangements: projection.getSelectedTaskArrangements(),
    taskActivities: projection.getTaskActivities(),
  };
  let cachedTaskActivityBuckets = groupTaskActivities(projection.getTaskActivities());
  let cachedTags = summarizeTags(cachedFocusSnapshot.tasks);
  const refreshFocusSnapshot = (): void => {
    const taskActivities = projection.getTaskActivities();
    cachedFocusSnapshot = {
      projects: cachedProjects,
      objectives: projection.getObjectives(),
      keyResults: projection.getKeyResults(),
      tasks: projection.getTasks(),
      openTasks: projection.getOpenTasks(),
      workflowColumns: projection.getWorkflowColumns(),
      smartLists: projection.getSmartLists(),
      taskArrangements: projection.getTaskArrangements(),
      selectedTaskArrangements: projection.getSelectedTaskArrangements(),
      taskActivities,
    };
    cachedTaskActivityBuckets = groupTaskActivities(taskActivities);
    cachedTags = summarizeTags(cachedFocusSnapshot.tasks);
  };
  projection.subscribe(refreshFocusSnapshot);

  return {
    doc,
    projection,
    whenSynced,
    useSyncStatus: () =>
      useSyncExternalStore(
        subscribeSync,
        () => sync.status,
        () => sync.status,
      ),
    useProjects: () =>
      useSyncExternalStore(
        subscribeProjects,
        () => cachedProjects,
        () => cachedProjects,
      ),
    useAllProjects: () =>
      useSyncExternalStore(
        subscribeProjects,
        () => cachedAllProjects,
        () => cachedAllProjects,
      ),
    useActiveProject: () =>
      useSyncExternalStore(
        (listener) => {
          // Re-render when either the active id changes OR the active
          // project's data (name, etc.) changes.
          const offActive = subscribeActive(listener);
          const offProjects = subscribeProjects(listener);
          return () => {
            offActive();
            offProjects();
          };
        },
        () => findProject(activeProjectId) ?? inboxOrFirst(),
        () => findProject(activeProjectId) ?? inboxOrFirst(),
      ),
    useTasks: () =>
      useSyncExternalStore(
        subscribeTasks,
        () => cachedActiveTasks,
        () => cachedActiveTasks,
      ),
    useOkrSnapshot: () =>
      useSyncExternalStore(
        projection.subscribe,
        () => cachedOkrSnapshot,
        () => cachedOkrSnapshot,
      ),
    useFocusSnapshot: () =>
      useSyncExternalStore(
        (listener) => {
          const offProjection = projection.subscribe(listener);
          const offProjects = subscribeProjects(listener);
          return () => {
            offProjection();
            offProjects();
          };
        },
        () => cachedFocusSnapshot,
        () => cachedFocusSnapshot,
      ),
    useWorkflowColumns: () =>
      useSyncExternalStore(
        projection.subscribe,
        () => cachedFocusSnapshot.workflowColumns,
        () => cachedFocusSnapshot.workflowColumns,
      ),
    useSmartLists: () =>
      useSyncExternalStore(
        projection.subscribe,
        () => cachedFocusSnapshot.smartLists,
        () => cachedFocusSnapshot.smartLists,
      ),
    useTaskArrangements: () =>
      useSyncExternalStore(
        projection.subscribe,
        () => cachedFocusSnapshot.taskArrangements,
        () => cachedFocusSnapshot.taskArrangements,
      ),
    useSelectedTaskArrangements: () =>
      useSyncExternalStore(
        projection.subscribe,
        () => cachedFocusSnapshot.selectedTaskArrangements,
        () => cachedFocusSnapshot.selectedTaskArrangements,
      ),
    useTags: () =>
      useSyncExternalStore(
        projection.subscribe,
        () => cachedTags,
        () => cachedTags,
      ),
    useTaskActivities: (taskId: string | undefined) =>
      useSyncExternalStore(
        projection.subscribe,
        () =>
          taskId
            ? (cachedTaskActivityBuckets.get(taskId) ?? EMPTY_TASK_ACTIVITIES)
            : EMPTY_TASK_ACTIVITIES,
        () =>
          taskId
            ? (cachedTaskActivityBuckets.get(taskId) ?? EMPTY_TASK_ACTIVITIES)
            : EMPTY_TASK_ACTIVITIES,
      ),
    keyResultProgress: (id: string): number => projection.keyResultProgress(id),
    expectedKeyResultProgress: (id: string, now: number): number =>
      projection.expectedKeyResultProgress(id, now),
    keyResultLag: (id: string, now: number): number => projection.keyResultLag(id, now),
    objectiveProgress: (id: string): number => projection.objectiveProgress(id),
    expectedObjectiveProgress: (id: string, now: number): number =>
      projection.expectedObjectiveProgress(id, now),
    objectiveLag: (id: string, now: number): number => projection.objectiveLag(id, now),
    isTaskBlocked: (id: string): boolean => projection.isTaskBlocked(id),
    hasChildTasks: (id: string): boolean => projection.hasChildTasks(id),
    childTasks: (parentTaskId: string): Task[] =>
      sortByCreated(projection.childTasks(parentTaskId)),
    tasksMatchingQuery: (query: string): Task[] => {
      const childTaskIds = new Set(
        projection
          .getTasks()
          .filter((task) => projection.hasChildTasks(task.id))
          .map((task) => task.id),
      );
      const compiled = compileQuery(query, { childTaskIds });
      if ('error' in compiled) return [];
      return sortByCreated(
        projection
          .getTasks()
          .filter(
            (task) =>
              task.trashedAt === undefined &&
              cachedProjects.some((project) => project.id === task.projectId) &&
              compiled.match(task),
          ),
      );
    },
    setActiveProject,
    createProject: (name: string): Project | undefined => {
      const trimmed = name.trim();
      if (!trimmed) return undefined;
      const project = createProjectInDoc(doc, { name: trimmed });
      setActiveProject(project.id);
      return project;
    },
    renameProject: (id: string, name: string): void => {
      const trimmed = name.trim();
      if (!trimmed) return;
      renameProjectInDoc(doc, id, trimmed);
    },
    archiveProject: (id: string): void => {
      if (id === INBOX_PROJECT_ID) return; // protect Inbox
      archiveProjectInDoc(doc, id);
      if (activeProjectId === id) setActiveProject(INBOX_PROJECT_ID);
    },
    unarchiveProject: (id: string): void => {
      unarchiveProjectInDoc(doc, id);
    },
    reorderProjects: (orderedVisibleIds: string[]): void => {
      const hiddenIds = cachedAllProjects
        .filter((project) => !orderedVisibleIds.includes(project.id))
        .map((project) => project.id);
      reorderProjectsInDoc(doc, [...orderedVisibleIds, ...hiddenIds]);
    },
    createTaskFromSmartAdd: (text: string): boolean => {
      if (!text.trim()) return false;
      const now = new Date();
      const parsed = parseSmartAdd(text, { now });
      if (parsed.error !== undefined) return false;
      doc.transact(() => {
        const task = createTask(
          doc,
          smartAddToCreateInput(parsed, activeProjectId, {
            defaultScheduledAt: startOfDay(now),
          }),
        );
        createTaskActivityInDoc(doc, {
          taskId: task.id,
          type: 'created',
          message: 'Task created',
        });
      });
      return true;
    },
    toggleTaskComplete: (id: string): void => {
      // Read the task before opening the transaction; every later step works
      // from this local snapshot (the projection does not observe writes
      // until the transaction commits).
      const task = projection.getTask(id);
      if (!task) return;
      doc.transact(() => {
        if (task.completedAt !== undefined) {
          reopenTask(doc, id);
          createTaskActivityInDoc(doc, {
            taskId: id,
            type: 'reopened',
            message: 'Task reopened',
          });
        } else {
          const completedAt = Date.now();
          completeTask(doc, id, { now: completedAt });
          createTaskActivityInDoc(
            doc,
            {
              taskId: id,
              type: 'completed',
              message: 'Task completed',
            },
            { now: completedAt },
          );
          const rollover = buildRecurringTaskRollover(task, completedAt);
          if (rollover) {
            createTask(doc, rollover, { now: completedAt + 1 });
          }
        }
      });
    },
    renameTask: (id: string, title: string): void => {
      const trimmed = title.trim();
      if (!trimmed) return;
      updateTask(doc, id, { title: trimmed });
    },
    updateTask: (id: string, patch: UpdateTaskInput): void => {
      const before = projection.getTask(id);
      if (!before) return;
      const next = { ...patch };
      if (next.title !== undefined) {
        const trimmed = next.title.trim();
        if (!trimmed) return;
        next.title = trimmed;
      }
      if (next.location !== undefined) {
        next.location = next.location.trim() || undefined;
      }
      if (next.notes !== undefined) {
        next.notes = next.notes.trim() || undefined;
      }
      // `before` was read from the projection above, so the activity diff is
      // computed from local state, not a mid-transaction projection read.
      doc.transact(() => {
        updateTask(doc, id, next);
        recordTaskChangeActivities(doc, before, next);
      });
    },
    createChildTask: (parentTaskId: string, title: string): Task | undefined => {
      const parent = projection.getTask(parentTaskId);
      const trimmed = title.trim();
      if (!parent || !trimmed || parent.parentTaskId !== undefined) return undefined;
      const parsed = parseSmartAdd(trimmed, { now: new Date() });
      const input: CreateTaskInput =
        parsed.error === undefined
          ? smartAddToCreateInput(parsed, parent.projectId)
          : {
              title: trimmed,
              projectId: parent.projectId,
            };
      // The activities only depend on `parent` (read before the transaction)
      // and `child` (returned by createTask), so no projection re-read is
      // needed inside the batch.
      return doc.transact(() => {
        const child = createTask(doc, {
          ...input,
          projectId: parent.projectId,
          parentTaskId: parent.id,
          workflowState: input.workflowState ?? parent.workflowState,
        });
        createTaskActivityInDoc(doc, {
          taskId: child.id,
          type: 'created',
          message: `Child task created under "${parent.title}"`,
        });
        createTaskActivityInDoc(doc, {
          taskId: parent.id,
          type: 'parent_changed',
          message: `Child task added: "${child.title}"`,
        });
        return child;
      });
    },
    duplicateTask: (id: string): Task | undefined => {
      const source = projection.getTask(id);
      if (!source || source.trashedAt !== undefined) return undefined;
      return doc.transact(() => {
        const duplicate = createTask(doc, buildDuplicateTaskInput(source));
        createTaskActivityInDoc(doc, {
          taskId: duplicate.id,
          type: 'created',
          message: `Task duplicated from "${source.title}"`,
        });
        return duplicate;
      });
    },
    reorderTasks: (orderedVisibleIds: string[]): void => {
      reorderTasksInDoc(doc, orderedVisibleIds);
    },
    completeTaskWithChildren: (id: string, completeChildren: boolean): void => {
      // Read the task and its children from the projection before opening
      // the transaction — nothing in this action changes the parent/child
      // relationship, so the pre-write snapshot stays valid throughout.
      const task = projection.getTask(id);
      if (!task) return;
      const children = completeChildren ? projection.childTasks(id) : [];
      const completedAt = Date.now();
      doc.transact(() => {
        if (task.completedAt === undefined) {
          completeTask(doc, id, { now: completedAt });
          createTaskActivityInDoc(
            doc,
            { taskId: id, type: 'completed', message: 'Task completed' },
            { now: completedAt },
          );
        }
        for (const child of children) {
          if (child.completedAt !== undefined) continue;
          completeTask(doc, child.id, { now: completedAt + 1 });
          createTaskActivityInDoc(
            doc,
            { taskId: child.id, type: 'completed', message: 'Child task completed with parent' },
            { now: completedAt + 1 },
          );
        }
      });
    },
    setTaskReminder: (id: string, reminderAt: number | undefined): void => {
      updateTask(doc, id, { reminderAt });
    },
    removeTask: (
      id: string,
      options: { deleteSeries?: boolean; generateNext?: boolean } = {},
    ): void => {
      const task = projection.getTask(id);
      if (!task) return;
      const trashedAt = Date.now();
      // Snapshot the task list before writing: inside the transaction the
      // projection has not observed the trashes yet, so a mid-transaction
      // re-read would miss them. The pre-write snapshot is equivalent to the
      // old post-trash re-read because in the generateNext branch
      // (deleteSeries=false) the only task trashed in this action is `task`
      // itself, which hasLaterOpenRecurringInstance already excludes by id.
      const allTasks = projection.getTasks();
      const targets = options.deleteSeries ? getRecurringSeriesTasks(allTasks, task) : [task];
      doc.transact(() => {
        for (const target of targets) {
          if (target.trashedAt !== undefined) continue;
          trashTask(doc, target.id, { now: trashedAt });
        }
        if (!options.deleteSeries && options.generateNext) {
          if (!hasLaterOpenRecurringInstance(allTasks, task)) {
            const rollover = buildRecurringTaskRollover(task, trashedAt);
            if (rollover) createTask(doc, rollover, { now: trashedAt + 1 });
          }
        }
      });
    },
    restoreTask: (id: string): void => {
      restoreTaskInDoc(doc, id);
    },
    undoLastChange: (): void => {
      undoManager.undo();
    },
    useReminderPreferences: () =>
      useSyncExternalStore(
        subscribeReminderPreferences,
        () => cachedReminderPreferences,
        () => cachedReminderPreferences,
      ),
    updateReminderPreferences: (patch) => {
      cachedReminderPreferences = updateReminderPreferencesInDoc(doc, patch);
    },
    useUiPreferences: () =>
      useSyncExternalStore(
        subscribeUiPreferences,
        () => cachedUiPreferences,
        () => cachedUiPreferences,
      ),
    updateUiPreferences: (patch) => {
      cachedUiPreferences = updateUiPreferencesInDoc(doc, patch);
    },
    createObjective: (input: CreateObjectiveInput): Objective | undefined => {
      const title = input.title.trim();
      if (!title) return undefined;
      return createObjectiveInDoc(doc, { ...input, title });
    },
    updateObjective: (id: string, patch: Partial<CreateObjectiveInput>): void => {
      const next = { ...patch };
      if (next.title !== undefined) {
        const trimmed = next.title.trim();
        if (!trimmed) return;
        next.title = trimmed;
      }
      updateObjectiveInDoc(doc, id, next);
    },
    createKeyResult: (input: CreateKeyResultInput): KeyResult | undefined => {
      const title = input.title.trim();
      if (!title) return undefined;
      return createKeyResultInDoc(doc, { ...input, title });
    },
    updateKeyResult: (id: string, patch: Partial<CreateKeyResultInput>): void => {
      const next = { ...patch };
      if (next.title !== undefined) {
        const trimmed = next.title.trim();
        if (!trimmed) return;
        next.title = trimmed;
      }
      updateKeyResultInDoc(doc, id, next);
    },
    linkTaskToKeyResult: (taskId: string, keyResultId: string): boolean => {
      try {
        linkTaskToKeyResultInDoc(doc, taskId, keyResultId);
        return true;
      } catch {
        return false;
      }
    },
    unlinkTaskFromKeyResult: (taskId: string): void => {
      unlinkTaskFromKeyResultInDoc(doc, taskId);
    },
    createCheckIn: (input: CreateCheckInInput): CheckIn | undefined => {
      const note = input.note.trim();
      if (!note) return undefined;
      return createCheckInInDoc(doc, { ...input, note });
    },
    createSmartList: (input: CreateSmartListInput): SmartList | undefined => {
      const name = input.name.trim();
      const query = input.query.trim();
      if (!name || 'error' in compileQuery(query)) return undefined;
      return createSmartListInDoc(doc, { ...input, name, query });
    },
    updateSmartList: (id: string, patch: UpdateSmartListInput): void => {
      const next = { ...patch };
      if (next.name !== undefined) {
        const name = next.name.trim();
        if (!name) return;
        next.name = name;
      }
      if (next.query !== undefined) {
        const query = next.query.trim();
        if ('error' in compileQuery(query)) return;
        next.query = query;
      }
      updateSmartListInDoc(doc, id, next);
    },
    removeSmartList: (id: string): void => {
      deleteSmartListInDoc(doc, id);
    },
    createTaskArrangement: (input: CreateTaskArrangementInput): TaskArrangement | undefined => {
      const name = input.name.trim();
      if (!name || !hasValidSort(input.sort)) return undefined;
      return createTaskArrangementInDoc(doc, { ...input, name });
    },
    updateTaskArrangement: (id: string, patch: UpdateTaskArrangementInput): void => {
      const next = { ...patch };
      if (next.name !== undefined) {
        const name = next.name.trim();
        if (!name) return;
        next.name = name;
      }
      if (!hasValidSort(next.sort)) return;
      updateTaskArrangementInDoc(doc, id, next);
    },
    removeTaskArrangement: (id: string): void => {
      deleteTaskArrangementInDoc(doc, id);
    },
    selectTaskArrangement: (
      listKey: string,
      arrangementId: string,
    ): SelectedTaskArrangement | undefined => {
      const trimmedListKey = listKey.trim();
      const trimmedArrangementId = arrangementId.trim();
      if (!trimmedListKey || !trimmedArrangementId) return undefined;
      return selectTaskArrangementInDoc(doc, trimmedListKey, trimmedArrangementId);
    },
    setTaskSubtaskDisplay: (
      listKey: string,
      subtaskDisplay: SubtaskDisplayT,
    ): SelectedTaskArrangement | undefined => {
      const trimmedListKey = listKey.trim();
      if (!trimmedListKey) return undefined;
      return setSelectedTaskSubtaskDisplayInDoc(doc, trimmedListKey, subtaskDisplay);
    },
    createWorkflowColumn: (input: CreateWorkflowColumnInput): WorkflowColumn | undefined => {
      const name = input.name.trim();
      if (!name) return undefined;
      return createWorkflowColumnInDoc(doc, { name });
    },
    renameWorkflowColumn: (id: string, name: string): void => {
      const trimmed = name.trim();
      if (!trimmed) return;
      renameWorkflowColumnInDoc(doc, id, trimmed);
    },
    reorderWorkflowColumns: (orderedIds: string[]): void => {
      reorderWorkflowColumnsInDoc(doc, orderedIds);
    },
    removeWorkflowColumn: (id: string, replacementId = 'backlog'): void => {
      deleteWorkflowColumnInDoc(doc, id, replacementId);
    },
    createTaskActivity: (input: CreateTaskActivityInput): TaskActivity | undefined => {
      const message = input.message.trim();
      if (!message) return undefined;
      return createTaskActivityInDoc(doc, { ...input, message });
    },
    deleteTaskActivity: (id: string): void => {
      deleteTaskActivityInDoc(doc, id);
    },
    updateTaskActivity: (id: string, message: string): void => {
      const trimmed = message.trim();
      if (trimmed) updateTaskActivityInDoc(doc, id, trimmed);
    },
    clearUserData: (): void => {
      // seedInbox and ensureDefaultWorkflowColumns read the Y.Doc directly
      // (not the projection), so they see the in-transaction clear.
      doc.transact(() => {
        clearContent(doc);
        seedInbox(doc);
        ensureDefaultWorkflowColumns(doc);
      });
    },
    exportData: (): ExportData => exportDoc(doc),
    importData: (data: ExportData): void => {
      importDataIntoDoc(doc, data);
    },
    resetLocalData: async (): Promise<void> => {
      await resetRemoteDataIfConfigured(options);
      if (persistence) {
        await persistence.clearData();
      }
      // After clearing IDB the doc is still in memory; dropping the
      // singleton forces the next getDocStore() to build a fresh one.
      sync.destroy();
      getProjectsMap(doc).unobserveDeep(projectsObserver);
      doc.getMap('reminderPreferences').unobserveDeep(reminderPreferencesObserver);
      doc.getMap('uiPreferences').unobserveDeep(uiPreferencesObserver);
      projection.destroy();
      undoManager.destroy();
      doc.destroy();
      singleton = undefined;
    },
  };
}

function filterAndSortProjects(projects: Project[]): Project[] {
  return projects
    .filter((p) => !p.archived)
    .sort(
      (a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) ||
        a.order - b.order ||
        a.createdAt - b.createdAt,
    );
}

function hasValidSort(sort: UpdateTaskArrangementInput['sort'] | undefined): boolean {
  return sort === undefined || sort.length > 0;
}

const ROOT_UNDO_TYPES = [
  'projects',
  'objectives',
  'keyResults',
  'checkIns',
  'tasks',
  'workflowColumns',
  'smartLists',
  'taskArrangements',
  'selectedTaskArrangements',
  'taskActivities',
  'reminderPreferences',
  'uiPreferences',
] as const;

function summarizeTags(tasks: Task[]): Array<{ tag: string; count: number }> {
  const counts = new Map<string, number>();
  for (const task of tasks) {
    if (task.completedAt !== undefined || task.trashedAt !== undefined) continue;
    for (const tag of task.tags) counts.set(tag, (counts.get(tag) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => a.tag.localeCompare(b.tag, undefined, { sensitivity: 'base' }));
}

async function resetRemoteDataIfConfigured(options: DocStoreOptions): Promise<void> {
  if (!options.syncUrl || !options.resetSecret) return;
  const base = options.syncUrl.replace(/^ws:/, 'http:').replace(/^wss:/, 'https:');
  const room = encodeURIComponent(options.syncRoom ?? 'default');
  try {
    await fetch(`${base.replace(/\/+$/, '')}/${room}?reset=1`, {
      method: 'POST',
      headers: { 'X-Reset-Secret': options.resetSecret },
    });
  } catch {
    // The local development reset should still work if the sync worker is offline.
  }
}

function groupTaskActivities(activities: TaskActivity[]): Map<string, TaskActivity[]> {
  const buckets = new Map<string, TaskActivity[]>();
  for (const activity of activities) {
    const bucket = buckets.get(activity.taskId) ?? [];
    bucket.push(activity);
    buckets.set(activity.taskId, bucket);
  }
  return buckets;
}

function startOfDay(value: Date): number {
  const date = new Date(value.getTime());
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

function recordTaskChangeActivities(doc: Y.Doc, before: Task, patch: UpdateTaskInput): void {
  const changes: Array<{ type: TaskActivity['type']; message: string }> = [];

  if (patch.workflowState !== undefined && patch.workflowState !== before.workflowState) {
    changes.push({ type: 'workflow_changed', message: 'Workflow column changed' });
  }
  if (patch.projectId !== undefined && patch.projectId !== before.projectId) {
    changes.push({ type: 'project_changed', message: 'Project changed' });
  }
  if (patch.parentTaskId !== undefined && patch.parentTaskId !== before.parentTaskId) {
    changes.push({ type: 'parent_changed', message: 'Parent task changed' });
  }
  if (patch.priority !== undefined && patch.priority !== before.priority) {
    changes.push({ type: 'priority_changed', message: `Priority changed to P${patch.priority}` });
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'dueAt') && patch.dueAt !== before.dueAt) {
    changes.push({ type: 'due_changed', message: 'Due date changed' });
  }
  if (
    Object.prototype.hasOwnProperty.call(patch, 'scheduledAt') &&
    patch.scheduledAt !== before.scheduledAt
  ) {
    changes.push({ type: 'scheduled_changed', message: 'Scheduled date changed' });
  }

  for (const change of changes) {
    createTaskActivityInDoc(doc, { taskId: before.id, ...change });
  }
}

function buildRecurringTaskRollover(task: Task, completedAt: number): CreateTaskInput | undefined {
  if (!task.recurrence) return undefined;

  const referenceAt = task.recurrence.fromCompletion
    ? completedAt
    : (task.dueAt ?? task.scheduledAt ?? completedAt);
  const next = nextOccurrence(task.recurrence, new Date(referenceAt));
  if (!next) return undefined;

  const nextAnchor = next.getTime();
  const nextDueAt =
    task.dueAt !== undefined || task.scheduledAt === undefined ? nextAnchor : task.dueAt;
  const nextScheduledAt =
    task.scheduledAt !== undefined
      ? task.dueAt !== undefined
        ? task.scheduledAt + (nextAnchor - task.dueAt)
        : nextAnchor
      : undefined;
  const currentReminderAnchor = task.dueAt ?? task.scheduledAt;
  const nextReminderAnchor = nextDueAt ?? nextScheduledAt;
  const nextReminderAt =
    task.reminderAt !== undefined &&
    currentReminderAnchor !== undefined &&
    nextReminderAnchor !== undefined
      ? nextReminderAnchor - (currentReminderAnchor - task.reminderAt)
      : task.reminderAt;

  const input: CreateTaskInput = {
    title: task.title,
    projectId: task.projectId,
    workflowState: task.workflowState,
    priority: task.priority,
    tags: [...task.tags],
    recurrence: { ...task.recurrence, seriesId: task.recurrence.seriesId ?? task.id },
  };
  if (task.notes !== undefined) input.notes = task.notes;
  if (task.parentTaskId !== undefined) input.parentTaskId = task.parentTaskId;
  if (task.krId !== undefined) input.krId = task.krId;
  if (nextDueAt !== undefined) {
    input.dueAt = nextDueAt;
    if (task.dueAtHasTime !== undefined) input.dueAtHasTime = task.dueAtHasTime;
  }
  if (nextScheduledAt !== undefined) {
    input.scheduledAt = nextScheduledAt;
    if (task.scheduledAtHasTime !== undefined) {
      input.scheduledAtHasTime = task.scheduledAtHasTime;
    }
  }
  if (nextReminderAt !== undefined) input.reminderAt = nextReminderAt;
  if (task.estimateMinutes !== undefined) input.estimateMinutes = task.estimateMinutes;
  if (task.energy !== undefined) input.energy = task.energy;
  if (task.daypart !== undefined) input.daypart = task.daypart;
  if (task.location !== undefined) input.location = task.location;
  if (task.dependsOn !== undefined) input.dependsOn = [...task.dependsOn];

  return input;
}

function getRecurringSeriesTasks(tasks: Task[], source: Task): Task[] {
  if (!source.recurrence) return [source];
  return tasks.filter((task) => isSameRecurringSeries(task, source));
}

function hasLaterOpenRecurringInstance(tasks: Task[], source: Task): boolean {
  const sourceAnchor = source.dueAt ?? source.scheduledAt;
  if (sourceAnchor === undefined) return false;
  return tasks.some((task) => {
    if (task.id === source.id || task.completedAt !== undefined || task.trashedAt !== undefined) {
      return false;
    }
    if (!isSameRecurringSeries(task, source)) return false;
    const taskAnchor = task.dueAt ?? task.scheduledAt;
    return taskAnchor !== undefined && taskAnchor > sourceAnchor;
  });
}

function isSameRecurringSeries(task: Task, source: Task): boolean {
  if (!task.recurrence || !source.recurrence) return false;
  const sourceSeriesId = source.recurrence.seriesId;
  if (sourceSeriesId !== undefined && task.recurrence.seriesId !== undefined) {
    return task.recurrence.seriesId === sourceSeriesId;
  }
  return (
    task.title === source.title &&
    task.projectId === source.projectId &&
    task.parentTaskId === source.parentTaskId &&
    task.krId === source.krId &&
    task.recurrence.rrule === source.recurrence.rrule &&
    task.recurrence.fromCompletion === source.recurrence.fromCompletion
  );
}

function buildDuplicateTaskInput(task: Task): CreateTaskInput {
  const input: CreateTaskInput = {
    title: `${task.title} copy`,
    projectId: task.projectId,
    workflowState: task.workflowState,
    priority: task.priority,
    tags: [...task.tags],
  };
  if (task.notes !== undefined) input.notes = task.notes;
  if (task.parentTaskId !== undefined) input.parentTaskId = task.parentTaskId;
  if (task.krId !== undefined) input.krId = task.krId;
  if (task.dueAt !== undefined) {
    input.dueAt = task.dueAt;
    if (task.dueAtHasTime !== undefined) input.dueAtHasTime = task.dueAtHasTime;
  }
  if (task.scheduledAt !== undefined) {
    input.scheduledAt = task.scheduledAt;
    if (task.scheduledAtHasTime !== undefined) {
      input.scheduledAtHasTime = task.scheduledAtHasTime;
    }
  }
  if (task.reminderAt !== undefined) input.reminderAt = task.reminderAt;
  if (task.estimateMinutes !== undefined) input.estimateMinutes = task.estimateMinutes;
  if (task.energy !== undefined) input.energy = task.energy;
  if (task.daypart !== undefined) input.daypart = task.daypart;
  if (task.location !== undefined) input.location = task.location;
  if (task.owner !== undefined) input.owner = task.owner;
  if (task.url !== undefined) input.url = task.url;
  if (task.dependsOn !== undefined) input.dependsOn = [...task.dependsOn];
  if (task.recurrence !== undefined) input.recurrence = { ...task.recurrence };

  return input;
}

function createPersistence(options: DocStoreOptions, doc: Y.Doc): IndexeddbPersistence | undefined {
  if (options.dbName === null) return undefined;
  if (typeof indexedDB === 'undefined') return undefined;
  const name = options.dbName ?? DEFAULT_DB_NAME;
  return new IndexeddbPersistence(name, doc);
}
